import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
} from "react";
import {
  fittedModsDeep,
  traderStanding,
  TRADERS,
  type Inventory,
  type LoyaltyState,
  type OfferStanding,
  type QuestState,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import { InspectDialog, type InspectedItem } from "../inventory/inspect.js";
import { MenuSurface, type MenuEntry } from "../inventory/menu.js";
import { FloatingModDialog } from "../inventory/modDialog.js";
import { findOwnedWeapon, type OwnedWeapon } from "../inventory/modLogic.js";
import { ModPane } from "../inventory/modPane.js";
import { TileBody } from "../inventory/tile.js";
import {
  clearDialogs,
  clearMarketFocus,
  closeDialog,
  openDialog,
  raiseDialog,
  setMenuTab,
  setModWeapon,
  setWorkbenchOpen,
  useHud,
  type DialogKind,
  type OpenContainer,
} from "../store.js";
import { LINE, RADIUS_SM, TEXT_DIM } from "../theme.js";
import { formatEuros } from "./format.js";
import { IconClose, IconSearch } from "./icons.js";
import { CardTitle, EmptyState, Note, Num } from "./kit.js";
import { marketEntries } from "./marketEntries.js";
import {
  countByNode,
  DEFAULT_SORT,
  filterRows,
  focusLabel,
  focusName,
  marketNode,
  MARKET_ROOT_ID,
  MARKET_ROWS,
  MARKET_TREE,
  pathTo,
  sortRows,
  toggleSort,
  withinNode,
  type MarketRow,
  type MarketSort,
  type MarketSortKey,
} from "./marketLogic.js";
import { offerGateTags, offerProblem } from "./offerLogic.js";
import { planRows, shoppingList } from "../workbench/buildLogic.js";
import { LockGlyph } from "./traders.js";
import { TraderPortrait } from "./traderPortrait.js";

/**
 * EVERY SHELF IN THE VALLEY AS ONE LIST: the five traders' stock laid end to
 * end in one sortable column, so the carbine's three prices sit next to each
 * other. A purchase here IS the trader's own `buy` action — same
 * `offerProblem`, same `buyPrice`, same message. Selling stays on the Traders
 * tab: a sale is a conversation with one man. No stash is drawn, so every
 * Inspect and bench resolves against `MARKET_ROWS`; stash room is still
 * CHECKED by `offerProblem`.
 */

export interface MarketTabProps {
  euros: number;
  /** The account's level, for the shelves' `minLevel` gates. Handed down rather
   *  than read out of the store, for the reason `TradersTabProps.level` is. */
  level: number;
  /** The quest log — half of every trader's standing, and the whole of an
   *  `afterQuest` gate. See `TradersTabProps.quests`. */
  quests: readonly QuestState[];
  /** What has crossed each counter, the one stored half of standing. */
  loyalty: LoyaltyState;
  /** Needed by `ModPane` when a gun's read-only bench is opened off a row: the
   *  pane takes an inventory whether or not it is allowed to touch it. */
  inv: Inventory;
  /** Only ever read for ROOM (`offerProblem`) and handed to the bench. The
   *  market draws no grid of the player's own. */
  stash: OpenContainer | null;
}

/**
 * Rows per category across the WHOLE market, ignoring every filter — decides
 * which rows the sidebar draws at all. A category empty everywhere is hidden;
 * one emptied by the current search is dimmed and stays clickable.
 */
const MARKET_TOTALS: ReadonlyMap<string, number> = countByNode(MARKET_ROWS);

/**
 * The thumbnail box a row draws its item into — wide and short, fitted not
 * cropped, so one box serves a pistol and a sniper rifle.
 */
const THUMB_W = 56;
const THUMB_H = 34;

export function MarketTab(props: MarketTabProps): ReactElement {
  /**
   * The category branch on show. Opens on the root — nothing hidden before
   * anybody asked.
   */
  const [node, setNode] = useState<string>(MARKET_ROOT_ID);
  const [query, setQuery] = useState("");
  /** The sub-tab. `null` is "all traders" — an absent filter, not a trader. */
  const [traderId, setTraderId] = useState<string | null>(null);
  const [sort, setSort] = useState<MarketSort>(DEFAULT_SORT);
  /** The picked row, by `MarketRow.key`. Client-only and nothing has happened:
   *  selecting is how a player marks the line they are comparing against. */
  const [selected, setSelected] = useState<string | null>(null);
  /** The list's right-click menu. Rows are catalogue data with no `ItemSource`
   *  to name them, so this screen words its own entries — see `traders.tsx`. */
  const [menu, setMenu] = useState<RowMenu | null>(null);

  /** The floating bench measures itself against this screen rather than against
   *  the whole menu, exactly as the trader tab's does. */
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const stashGrid = props.stash?.grid ?? null;

  /**
   * What the player asked for on their way here, or `null` (see `MarketFocus`).
   * Folded into `scoped` so the sidebar's counts answer the focused question
   * too.
   */
  const focus = useHud((s) => s.marketFocus);

  /**
   * How many parts the workbench's plan is still short of. Recomputed from the
   * LIVE kit, so the chip counts down as the player buys — the list the focus
   * arrived with would keep naming parts already in the stash.
   */
  const workbench = useHud((s) => s.workbench);
  const stillToBuy = useMemo(() => {
    if (workbench === null) return null;
    const weapon = findOwnedWeapon(props.inv, workbench.weaponIid, props.stash);
    if (weapon === null) return null;
    const rows = planRows(weapon.def, weapon.item, workbench.plan, props.inv, props.stash);
    return shoppingList(rows).reduce((sum, line) => sum + line.count, 0);
  }, [workbench, props.inv, props.stash]);

  /**
   * Where the player stands with every trader, once per render — a row's gates
   * are its trader's gates. Built by the shared `traderStanding`.
   */
  const standings = useMemo(() => {
    const map = new Map<string, OfferStanding>();
    for (const trader of TRADERS) {
      map.set(trader.id, traderStanding(props.loyalty, props.quests, props.level, trader.id));
    }
    return map;
  }, [props.loyalty, props.quests, props.level]);

  /**
   * The rows the SUB-TAB AND SEARCH leave, before the category. The sidebar
   * counts from this list, so siblings of the selected branch do not read zero.
   */
  const scoped = useMemo(
    () => filterRows(MARKET_ROWS, { node: MARKET_ROOT_ID, query, traderId, focus }),
    [query, traderId, focus],
  );
  const counts = useMemo(() => countByNode(scoped), [scoped]);
  const rows = useMemo(
    () => sortRows(scoped.filter((row) => withinNode(row.leaf, node)), sort),
    [scoped, node, sort],
  );

  /**
   * A focus arriving drops search, sub-tab, category and selection — a
   * narrowing left from the last visit would show the player nothing. Keyed on
   * `seq`, not the object or id: asking for the same item again is a second
   * arrival.
   */
  const focusSeq = focus?.seq ?? null;
  useEffect(() => {
    if (focusSeq === null) return;
    setNode(MARKET_ROOT_ID);
    setQuery("");
    setTraderId(null);
    setSelected(null);
  }, [focusSeq]);

  // --- The dialogs: the trader tab's re-resolve-or-die pass with one source.
  // An Inspect ref can only name a market instance — a row's item or a part
  // inside one of its guns. A ref that stops resolving closes.
  const dialogs = useHud((s) => s.dialogs);
  const resolved = dialogs.map((ref) => {
    const row = ref.t === "inspect" ? rowByIid(ref.iid) : null;
    const part = ref.t === "inspect" && row === null ? marketPartInspect(ref.iid) : null;
    return {
      ref,
      inspected: row === null ? part : ({ item: row.item, def: row.def } satisfies InspectedItem),
      // The ASKING price, only for a row: a part inside a market gun is not for
      // sale on its own.
      asking: row?.price ?? null,
    };
  });
  const goneKey = resolved
    .filter((d) => d.inspected === null)
    .map((d) => `${d.ref.t}:${d.ref.iid}`)
    .join(",");
  useEffect(() => {
    if (goneKey === "") return;
    for (const key of goneKey.split(",")) {
      const idx = key.indexOf(":");
      closeDialog(key.slice(0, idx) as DialogKind, key.slice(idx + 1));
    }
  }, [goneKey]);

  // The read-only Werkbank's subject, dressed as an `OwnedWeapon` with no area
  // —
  // all the pane ever reads it for.
  const modWeaponIid = useHud((s) => s.modWeaponIid);
  const benchRow = modWeaponIid === null ? null : rowByIid(modWeaponIid);
  const benchDef = benchRow?.def ?? null;
  const modWeapon: OwnedWeapon | null =
    benchRow === null || benchDef === null || benchDef.kind !== "weapon"
      ? null
      : { item: benchRow.item, def: benchDef, area: null };
  const modGone = modWeaponIid !== null && modWeapon === null;
  useEffect(() => {
    // A stale cleanup queued for a bench that has already moved on must not
    // close a newer selection — the same guard the trader tab keeps.
    if (modGone && useHud.getState().modWeaponIid === modWeaponIid) setModWeapon(null);
  }, [modGone, modWeaponIid]);

  // Leaving the market packs every window away: the bench could be standing on
  // a gun that exists only in this screen's catalogue.
  useEffect(() => {
    return () => {
      setModWeapon(null);
      clearDialogs();
    };
  }, []);

  // --- Buying: re-asked when the money would move, never trusted from the
  // render — the stash fills up while a menu is open.

  const problemOf = (row: MarketRow): string | null => {
    // Unreachable fallback (every row's trader is in `TRADERS`), but the honest
    // answer is to work it out, never to assume the door is open.
    const standing =
      standings.get(row.trader.id) ??
      traderStanding(props.loyalty, props.quests, props.level, row.trader.id);
    return offerProblem(row.trader, row.def.id, props.euros, stashGrid, standing);
  };

  const buyRow = (row: MarketRow): void => {
    if (problemOf(row) !== null) return;
    uiActions().buy(row.trader.id, row.def.id);
  };

  /**
   * Inspect through the ROW'S OWN instance. A gun goes to the READ-ONLY bench —
   * the rifle is still the trader's.
   */
  const inspectRow = (row: MarketRow): void => {
    setMenu(null);
    if (row.def.kind === "weapon") setModWeapon(row.item.iid);
    else openDialog("inspect", row.item.iid);
  };

  /**
   * The list's right-click menu. Inspect always; the purchase is ABSENT rather
   * than shut when it cannot be made.
   */
  const rowEntries = (row: MarketRow): readonly MenuEntry[] => {
    const entries: MenuEntry[] = [
      {
        label: "Inspect",
        run: () => {
          inspectRow(row);
        },
      },
      // The list can narrow itself: a row is one man's price, and the two
      // entries are "show me the other four" and "show me what goes with it".
      ...marketEntries(row.def.id),
    ];
    if (problemOf(row) === null) {
      entries.push({
        label: `Buy · ${formatEuros(row.price)}`,
        run: () => {
          buyRow(row);
        },
      });
    }
    return entries;
  };

  /**
   * The read-only bench's menu on a fitted part: look at it, and go looking for
   * it — it may be on a shelf elsewhere in this list.
   */
  const partEntries = (iid: string): readonly MenuEntry[] => {
    const entries: MenuEntry[] = [
      {
        label: "Inspect",
        run: () => {
          setMenu(null);
          openDialog("inspect", iid);
        },
      },
    ];
    const part = marketPartInspect(iid);
    if (part !== null) entries.push(...marketEntries(part.def.id));
    return entries;
  };

  /**
   * What the open menu says. A row whose key no longer resolves gets an EMPTY
   * list, which `MenuSurface` renders as no menu.
   */
  const menuEntries = (open: RowMenu): readonly MenuEntry[] => {
    if (open.about.t === "part") return partEntries(open.about.iid);
    const key = open.about.key;
    const row = MARKET_ROWS.find((r) => r.key === key) ?? null;
    return row === null ? [] : rowEntries(row);
  };

  const clearSearch = (): void => {
    setQuery("");
    searchRef.current?.focus();
  };

  const path = pathTo(node);

  return (
    <div
      ref={rootRef}
      style={{
        display: "flex",
        gap: 14,
        alignItems: "stretch",
        width: "100%",
        maxWidth: 1200,
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        // The floating bench centres itself on the nearest positioned ancestor,
        // which has to be this screen and not the whole menu.
        position: "relative",
      }}
    >
      <aside
        className="mm-card mm-card-tight"
        style={{
          flex: "0 0 240px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <CardTitle aside={<Num tone="dim">{MARKET_ROWS.length}</Num>}>MARKET</CardTitle>

        {/* The field, with the magnifier inside it and the × on top of its own
            right edge — see `.mm-field`. It grows across a row by default; this
            sidebar is a column, where growing would stretch it. */}
        <div className="mm-field" style={{ flex: "0 0 auto" }}>
          <IconSearch size={14} />
          <input
            ref={searchRef}
            className="metainput"
            // `text` rather than `search`: Chrome draws its own clear button in
            // a
            // search field, and the × beside this one is that.
            type="text"
            aria-label="Search the market"
            placeholder="Search…"
            spellCheck={false}
            autoComplete="off"
            value={query}
            onChange={(ev) => {
              setQuery(ev.target.value);
            }}
            onKeyDown={(ev) => {
              // Escape empties the FIELD; the game's own listener would
              // otherwise
              // walk the player out of the shop.
              if (ev.key !== "Escape") return;
              ev.preventDefault();
              ev.stopPropagation();
              setQuery("");
            }}
          />
          {query === "" ? null : (
            <button
              type="button"
              className="mm-field-clear"
              aria-label="Clear the search"
              title="Clear the search"
              onClick={clearSearch}
            >
              <IconClose size={13} />
            </button>
          )}
        </div>

        {/* THE TREE. One button per node, and the only thing that decides which
            branch is open is the SELECTION: a category's children are drawn
            exactly when it is on the path to the selected node, so picking one
            category closes the last without a second piece of state that could
            come to disagree with the first. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <CategoryRow
            id={MARKET_ROOT_ID}
            label="All"
            depth={0}
            count={counts.get(MARKET_ROOT_ID) ?? 0}
            selected={node === MARKET_ROOT_ID}
            onPick={setNode}
          />
          {MARKET_TREE.filter((top) => (MARKET_TOTALS.get(top.id) ?? 0) > 0).map((top) => (
            <Fragment key={top.id}>
              <CategoryRow
                id={top.id}
                label={top.label}
                depth={0}
                count={counts.get(top.id) ?? 0}
                selected={node === top.id}
                onPick={setNode}
              />
              {!path.includes(top.id)
                ? null
                : top.children
                    .filter((child) => (MARKET_TOTALS.get(child.id) ?? 0) > 0)
                    .map((child) => (
                      <CategoryRow
                        key={child.id}
                        id={child.id}
                        label={child.label}
                        depth={1}
                        count={counts.get(child.id) ?? 0}
                        selected={node === child.id}
                        onPick={setNode}
                      />
                    ))}
            </Fragment>
          ))}
        </div>
      </aside>

      {/* The list and everything that narrows it, in one framed card: the strip
          and the header sit on its edge, so only the rows scroll. */}
      <div
        className="mm-card"
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          gap: 0,
          padding: 0,
          overflow: "hidden",
        }}
      >
        {/* THE SUB-TABS. One man or all of them, and "all of them" is the point
            of the screen, so it comes first and is where it opens. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            padding: "8px 10px",
            borderBottom: `1px solid ${LINE}`,
          }}
        >
          <button
            type="button"
            className="metasubtab"
            aria-selected={traderId === null}
            onClick={() => {
              setTraderId(null);
            }}
          >
            All traders
          </button>
          {TRADERS.map((trader) => (
            <button
              key={trader.id}
              type="button"
              className="metasubtab"
              aria-selected={traderId === trader.id}
              onClick={() => {
                setTraderId(trader.id);
              }}
            >
              <span className="mm-portrait" style={{ display: "flex" }}>
                <TraderPortrait trader={trader} size={20} style={{ borderRadius: "inherit" }} />
              </span>
              {trader.name}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <Num tone="dim" style={{ fontSize: 11, paddingRight: 4 }}>
            {rows.length} offer{rows.length === 1 ? "" : "s"}
          </Num>
        </div>

        {/* THE FOCUS, SAID OUT LOUD. A list this short with no explanation on it
            reads as a market that has lost its stock, so the chip names the
            narrowing and the thing it is about — and carries the way out of it,
            because the player who wants the whole market back must not have to
            work out that they got here from another tab. */}
        {focus === null ? null : (
          <div className="metafocus" data-market-focus={focus.t}>
            <span style={{ color: TEXT_DIM }}>{focusLabel(focus)}</span>
            <span style={{ color: "#e3eaf1" }}>
              {focus.t === "build" && stillToBuy !== null
                ? `${stillToBuy} still to buy`
                : focusName(focus)}
            </span>
            {/* The way BACK. A player sent here by the bench is mid-build: the
                market is a detour, not a destination. */}
            {focus.t === "build" ? (
              <button
                type="button"
                className="metabtn metabtn-sm"
                onClick={() => {
                  setWorkbenchOpen(true);
                  setMenuTab("gear");
                }}
              >
                Back to workbench
              </button>
            ) : null}
            <button
              type="button"
              className="metabtn metabtn-sm metabtn-ghost"
              aria-label="Clear the filter"
              title="Clear the filter"
              onClick={clearMarketFocus}
            >
              ×
            </button>
          </div>
        )}

        <div className="metarow metarow-head">
          <span />
          <SortButton label="Item" sortKey="name" sort={sort} onSort={setSort} />
          <SortButton label="Trader" sortKey="trader" sort={sort} onSort={setSort} />
          <SortButton label="Price" sortKey="price" sort={sort} onSort={setSort} />
          <span />
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {rows.length === 0 ? (
            <EmptyState
              icon={<IconSearch />}
              title="Nothing on the market matches."
              action={
                query === "" ? undefined : (
                  <button type="button" className="metabtn metabtn-sm" onClick={clearSearch}>
                    Clear search
                  </button>
                )
              }
            />
          ) : (
            rows.map((row) => (
              <Row
                key={row.key}
                row={row}
                standing={standings.get(row.trader.id) ?? null}
                problem={problemOf(row)}
                selected={row.key === selected}
                onSelect={() => {
                  setSelected(row.key);
                }}
                onBuy={() => {
                  buyRow(row);
                }}
                onInspect={() => {
                  inspectRow(row);
                }}
                onMenu={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  setMenu({ px: ev.clientX, py: ev.clientY, about: { t: "row", key: row.key } });
                }}
              />
            ))
          )}
        </div>
      </div>

      {/* THE WERKBANK, read-only: a gun on the market shows what it is made of
          and what it would do, and nothing that would change it. */}
      {modWeapon === null ? null : (
        <FloatingModDialog
          title={`MARKET › ${modWeapon.def.name.toUpperCase()}`}
          rootRef={rootRef}
          header={
            benchRow === null ? undefined : (
              <span style={{ fontSize: 11, color: TEXT_DIM }}>
                {benchRow.trader.name} · {formatEuros(benchRow.price)}
              </span>
            )
          }
          onClose={() => {
            setModWeapon(null);
          }}
        >
          {(contentScale) => (
            <ModPane
              inv={props.inv}
              container={props.stash}
              weapon={modWeapon}
              containerLabel="Stash"
              containerOwned
              readOnly
              previewScale={contentScale}
              onPartContextMenu={(ev, iid) => {
                ev.preventDefault();
                ev.stopPropagation();
                setMenu({ px: ev.clientX, py: ev.clientY, about: { t: "part", iid } });
              }}
            />
          )}
        </FloatingModDialog>
      )}

      {/* AFTER the bench and in ONE list, for the reasons the trader tab spells
          out: the two are siblings at the same z-index, and the array order is
          the stacking order. */}
      {resolved.map(({ ref, inspected, asking }, i) =>
        inspected === null ? null : (
          <InspectDialog
            key={`${ref.t}:${ref.iid}`}
            nudge={i * 26}
            onRaise={() => {
              raiseDialog(ref.t, ref.iid);
            }}
            subject={inspected}
            // No trader, ever: "Sells for" is a fact about something already in
            // the stash, and nothing on this screen is.
            trader={null}
            buyPrice={asking}
            onClose={() => {
              closeDialog("inspect", ref.iid);
            }}
          />
        ),
      )}

      {menu === null ? null : (
        <MenuSurface
          px={menu.px}
          py={menu.py}
          entries={menuEntries(menu)}
          onClose={() => {
            setMenu(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Where the right-click menu was asked for: a row, or a part inside one of the
 * market's guns. One state, so only one menu stands.
 */
interface RowMenu {
  /** Viewport pixels. */
  px: number;
  py: number;
  about: { t: "row"; key: string } | { t: "part"; iid: string };
}

/** The market row whose own instance carries `iid`. */
function rowByIid(iid: string): MarketRow | null {
  return MARKET_ROWS.find((row) => row.item.iid === iid) ?? null;
}

/**
 * The PART `iid` names inside one of the market's guns, for an Inspect off the
 * read-only bench. Every weapon row's tree is walked at `fittedModsDeep` depth;
 * the mint gives every factory part its own id.
 */
function marketPartInspect(iid: string): InspectedItem | null {
  for (const row of MARKET_ROWS) {
    if (row.def.kind !== "weapon") continue;
    const hit = fittedModsDeep(row.def, row.item).find((mod) => mod.item.iid === iid);
    if (hit !== undefined) return { item: hit.item, def: hit.def };
  }
  return null;
}

/**
 * One sidebar node: name left, row count right. Never disabled — an emptied
 * category is still the way to widen.
 */
function CategoryRow(props: {
  id: string;
  label: string;
  depth: 0 | 1;
  count: number;
  selected: boolean;
  onPick: (id: string) => void;
}): ReactElement {
  return (
    <button
      type="button"
      className="metacat"
      data-depth={props.depth}
      data-empty={props.count === 0 ? "true" : "false"}
      aria-selected={props.selected}
      onClick={() => {
        props.onPick(props.id);
      }}
    >
      <span>{props.label}</span>
      <span className="metacat-count">{props.count}</span>
    </button>
  );
}

/** One column header. `aria-pressed` says which column the list is sorted by,
 *  and the arrow says which way. */
function SortButton(props: {
  label: string;
  sortKey: MarketSortKey;
  sort: MarketSort;
  onSort: (sort: MarketSort) => void;
}): ReactElement {
  const active = props.sort.key === props.sortKey;
  return (
    <button
      type="button"
      className="metarow-sort"
      aria-pressed={active}
      onClick={() => {
        props.onSort(toggleSort(props.sort, props.sortKey));
      }}
    >
      {props.label}
      {active ? (props.sort.dir === 1 ? " ▲" : " ▼") : ""}
    </button>
  );
}

/**
 * ONE LINE OF THE MARKET. A `<div>` with a `role`, since a button cannot hold
 * the Buy button. Shelf gestures: click SELECTS, Ctrl+click BUYS, double-click
 * INSPECTS, right-click the menu. Unaffordable rows are washed out but never
 * inert.
 */
function Row(props: {
  row: MarketRow;
  standing: OfferStanding | null;
  problem: string | null;
  selected: boolean;
  onSelect: () => void;
  onBuy: () => void;
  onInspect: () => void;
  onMenu: (ev: MouseEvent<HTMLDivElement>) => void;
}): ReactElement {
  const { row } = props;
  const gates = props.standing === null ? [] : offerGateTags(row.trader, row.def.id, props.standing);
  const leafLabel = marketNode(row.leaf)?.label ?? row.leaf;

  return (
    <div
      className="metarow"
      role="row"
      data-market-row={row.def.id}
      data-market-trader={row.trader.id}
      aria-selected={props.selected}
      data-dimmed={props.problem !== null}
      onClick={(ev) => {
        // Ctrl+click SPENDS, as on the shelf; a purchase is not a selection.
        if (ev.ctrlKey) props.onBuy();
        else props.onSelect();
      }}
      onDoubleClick={(ev) => {
        ev.preventDefault();
        props.onInspect();
      }}
      onContextMenu={props.onMenu}
    >
      {/* A div, because `TileBody` is one and fills whatever positioned box it
          is handed. */}
      <div
        style={{
          position: "relative",
          width: THUMB_W,
          height: THUMB_H,
          borderRadius: RADIUS_SM,
          border: `1px solid ${LINE}`,
          background: "rgba(255, 255, 255, 0.03)",
          overflow: "hidden",
        }}
      >
        <TileBody def={row.def} item={row.item} iconOnly cursor="default" />
      </div>

      <span style={{ minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 12.5,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.def.name}
        </span>
        {/* The shelf it is on, and — for the handful of lines that have any —
            what stands between the player and it. The gate chips are the tiles'
            own (`metaoffer-gate`), padlock and all, so a locked line reads the
            same in the list as it does in the shop window. */}
        <span style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
          <Note style={{ fontSize: 11 }}>{leafLabel}</Note>
          {gates.map((gate) => (
            <span
              key={gate.text}
              className={gate.shut ? "metaoffer-gate metaoffer-gate-shut" : "metaoffer-gate"}
              title={gate.title}
            >
              {gate.shut ? <LockGlyph /> : null}
              {gate.text}
            </span>
          ))}
        </span>
      </span>

      <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
        <span className="mm-portrait" style={{ display: "flex" }}>
          <TraderPortrait trader={row.trader} size={20} style={{ borderRadius: "inherit" }} />
        </span>
        <span
          style={{
            fontSize: 11.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.trader.name}
        </span>
      </span>

      <Num tone="accent" style={{ fontSize: 12, textAlign: "right" }}>
        {formatEuros(row.price)}
      </Num>

      <span className="metarow-buy" style={{ textAlign: "right" }}>
        <button
          type="button"
          className="metabtn metabtn-sm"
          data-market-buy=""
          disabled={props.problem !== null}
          title={props.problem ?? `Buy ${row.def.name}`}
          onClick={(ev) => {
            // The row's own click would otherwise select the line the button
            // just spent money on, which is one gesture doing two things.
            ev.stopPropagation();
            props.onBuy();
          }}
        >
          Buy
        </button>
      </span>
    </div>
  );
}
